import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as AlfredoStyles from "../styles/components/SearchInput";
import * as AlfredoInterface from "../interfaces";

const pages: AlfredoInterface.Pages[] = [
  {
    title: "Início",
    icon: "home",
    options: ["Dashboard", "Calendário"],
    path: "/",
  },
  {
    title: "Relatórios",
    icon: "picture_as_pdf",
    options: ["Contas", "Materiais"],
    path: "/reports",
    items: [
      {
        title: "Contas",
        icon: "list_alt",
        options: ["Clientes", "Ordens"],
        path: "/reports/orders",
      },
      {
        title: "Materiais",
        icon: "inventory",
        options: ["Estoque", "Movimentação"],
        path: "/reports/materials",
      },
    ],
  },
  {
    title: "Suprimentos",
    icon: "inventory",
    options: ["Compras", "Materiais"],
    path: "/supply",
    items: [
      {
        title: "Compras",
        icon: "local_shipping",
        options: ["Requisição", "Ordens"],
        path: "/supply/requisition",
      },
      {
        title: "Materiais",
        icon: "precision_manufacturing",
        options: ["Estoque", "Movimentação"],
        path: "/supply/moviment",
      },
    ],
  },
  {
    title: "Configuração",
    icon: "settings",
    options: ["Usuários", "Permissões"],
    path: "/settings",
  },
];

export function SearchInput() {
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLInputElement>(null);

  function filterItems(
    items: AlfredoInterface.Pages[] | undefined,
    groupTitle: string,
    searchText: string
  ): AlfredoInterface.Pages[] {
    if (!items) {
      return [];
    }

    return items.reduce((filtered, subGroup) => {
      if (
        groupTitle.toLowerCase().includes(searchText.toLowerCase()) ||
        subGroup.title.toLowerCase().includes(searchText.toLowerCase()) ||
        filterItems(subGroup.items, subGroup.title, searchText).length > 0
      ) {
        // Se o título do item pai ou do próprio item corresponde ao critério, inclua-o
        // e aplique o filtro aos subitens
        const newItem: AlfredoInterface.Pages = { ...subGroup };

        if (
          filterItems(subGroup.items, subGroup.title, searchText).length > 0
        ) {
          newItem.items = filterItems(
            subGroup.items,
            subGroup.title,
            searchText
          );
        }

        filtered.push(newItem);
      }

      return filtered;
    }, [] as AlfredoInterface.Pages[]);
  }

  const filteredResults = pages
    .map((group) => ({
      ...group,
      items: filterItems(group.items, group.title, searchValue),
    }))
    .filter(
      (result) =>
        result.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        result.items.length > 0
    );

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(e.target.value);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const key = e.key;

    if (key === "Escape") {
      setSearchValue("");
      searchInputRef.current && searchInputRef.current.blur();
    }
  }

  function handleClickLink() {
    searchInputRef.current && searchInputRef.current.blur();
  }

  async function handleDropdownMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    // Impedir que o input perca o foco quando o dropdown é clicado
    e.preventDefault();
  }

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "/") {
        searchInputRef.current && searchInputRef.current.focus();
      }
    };

    document.addEventListener("keyup", handleKeyPress);

    return () => {
      document.removeEventListener("keyup", handleKeyPress);
    };
  }, []);

  useEffect(() => {
    setSearchFocused(false);
  }, []);

  return (
    <AlfredoStyles.SearchWrapper>
      <link
        href="https://fonts.googleapis.com/icon?family=Material+Icons"
        rel="stylesheet"
      />
      <AlfredoStyles.Bar>
        <AlfredoStyles.Label>
          <AlfredoStyles.Icon>search</AlfredoStyles.Icon>
          <AlfredoStyles.Input
            placeholder="Digite / para pesquisar"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            onChange={handleInputChange}
            value={searchValue}
            onKeyDown={handleKeyDown}
            ref={searchInputRef}
          />
        </AlfredoStyles.Label>
      </AlfredoStyles.Bar>
      {searchFocused && (
        <AlfredoStyles.Dropdown
          ref={dropdownRef}
          onMouseDown={handleDropdownMouseDown}
        >
          <AlfredoStyles.NavWrapper>
            {filteredResults.map((group) => {
              return (
                <AlfredoStyles.MainGroup key={group.title}>
                  <p>
                    <Link to={group.path} onClick={handleClickLink}>
                      {group.title}
                    </Link>
                  </p>
                  {group.items?.map((subGroup) => (
                    <AlfredoStyles.SubGroup key={subGroup.title}>
                      <AlfredoStyles.SubGroupList level={0}>
                        <AlfredoStyles.SubGroupTitle
                          to={subGroup.path}
                          onClick={handleClickLink}
                        >
                          {subGroup.title}
                        </AlfredoStyles.SubGroupTitle>
                        {subGroup.items && (
                          <AlfredoStyles.SubGroupItems>
                            {subGroup.items.map((subItem: any) => (
                              <li key={subItem.title}>
                                <Link
                                  onClick={handleClickLink}
                                  to={subItem.path}
                                >
                                  {subItem.title}
                                </Link>
                              </li>
                            ))}
                          </AlfredoStyles.SubGroupItems>
                        )}
                      </AlfredoStyles.SubGroupList>
                    </AlfredoStyles.SubGroup>
                  ))}
                </AlfredoStyles.MainGroup>
              );
            })}
          </AlfredoStyles.NavWrapper>
        </AlfredoStyles.Dropdown>
      )}
    </AlfredoStyles.SearchWrapper>
  );
}
